import type { AppLinkProps } from '@/hooks/usePathHelper'
import dayjs from '@/lib/dayjs'
import type {
  AvailabilityCalendarOccurrence,
  AvailabilityTemporaryHours,
} from '@/lib/services/availability-changes/availability-changes.types'
import {
  formatAvailabilityEffectLabel,
  getAvailabilityChangeDateRangeDisplay,
  getChangeDateInputValue,
  getChangeEndDate,
  getChangeLocationLabel,
  type AvailabilityChangeFields,
} from '@/lib/services/availability-changes/availability-changes.utils'
import { cn } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import {
  Ban,
  CalendarSync,
  Clock,
  Flower2,
  MapPin,
  MousePointer2,
  ShoppingBasket,
  Truck,
} from 'lucide-react'
import type { Dayjs } from 'dayjs'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
} from 'react'
import { Button } from '../ui/button'
import { Badge } from '@/components/ui/badge'
import { popoverContentVariants } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import {
  getAvailabilityChangePresentation,
  type AvailabilityChangeEffectKey,
} from './availability-change.presentation'

/** A `YYYY-MM-DD` range of calendar days. */
export interface AvailabilityCalendarDateRange {
  startDate: string
  endDate: string
}

/** Viewport pixels a range selection ended at, where the quick-create chooser opens. */
export interface AvailabilityCalendarAnchor {
  left: number
  top: number
}

export interface AvailabilityCalendarRangeSelection extends AvailabilityCalendarDateRange {
  anchor: AvailabilityCalendarAnchor | null
}

/** A day button's pointer event, or the window's when a drag ends outside the calendar. */
interface SelectionAnchorEvent {
  clientX: number
  clientY: number
  currentTarget: (EventTarget & { getBoundingClientRect?: () => DOMRect }) | null
}

type DayPointerHandler = (dayKey: string, event: ReactPointerEvent<HTMLButtonElement>) => void

/** One week's slice of a change: the day columns it spans and the lane it is drawn in. */
interface CalendarSegment {
  change: AvailabilityCalendarOccurrence
  key: string
  startIndex: number
  endIndex: number
  continuesBefore: boolean
  continuesAfter: boolean
  lane: number
}

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const visibleEventLanes = 4
const eventLaneHeight = 30
const eventLaneOffset = 38

const effectLegendOrder: AvailabilityChangeEffectKey[] = [
  'store_closed',
  'pickup_delivery_unavailable',
  'pickup_paused',
  'delivery_paused',
  'designers_choice_only',
  'temporary_hours',
  'availability_change',
]

function getPrimaryEffectKey(change: AvailabilityChangeFields): AvailabilityChangeEffectKey {
  if (change.store_closed) return 'store_closed'
  if (change.pickup_paused && change.delivery_paused) return 'pickup_delivery_unavailable'
  if (change.pickup_paused) return 'pickup_paused'
  if (change.delivery_paused) return 'delivery_paused'
  if (change.designers_choice_only) return 'designers_choice_only'
  if (change.temporary_hours) return 'temporary_hours'

  return 'availability_change'
}

function parseChangeDate(value: string | null | undefined): Dayjs | null {
  const parsed = value ? dayjs(value) : null

  return parsed?.isValid() ? parsed : null
}

function parseChangeLocalDay(value: string | null | undefined): Dayjs | null {
  return parseChangeDate(getChangeDateInputValue(value) ?? value)
}

function getChangeStart(change: AvailabilityChangeFields): Dayjs | null {
  const value = change.start_date ?? change.date ?? change.starts_at

  return parseChangeLocalDay(value)
}

function getChangeEnd(change: AvailabilityChangeFields): Dayjs | null {
  const value = getChangeEndDate(change)

  return parseChangeLocalDay(value)
}

function formatCalendarDate(value: string | null | undefined): string | null {
  const parsed = parseChangeLocalDay(value)

  return parsed?.isValid() ? parsed.format('MMM D, YYYY') : null
}

function getChangeDateRange(change: AvailabilityChangeFields): string {
  return getAvailabilityChangeDateRangeDisplay(change, {
    separator: ' - ',
    formatDate: formatCalendarDate,
    preserveFiniteSingleDayBeforeDisplayIndefinite: true,
  }).label
}

function formatTemporaryHour(time: string | null | undefined): string | null {
  if (!time) {
    return null
  }

  const parsedTime = dayjs(`2000-01-01 ${time}`)

  return parsedTime.isValid() ? parsedTime.format('h:mm A') : time
}

function getTemporaryHoursPreviewLabels(change: AvailabilityChangeFields): string[] {
  const temporaryHours = change?.temporary_hours

  if (!temporaryHours) {
    return []
  }

  return (
    [
      ['pickup', 'Pickup'],
      ['delivery', 'Delivery'],
    ] satisfies [keyof AvailabilityTemporaryHours, string][]
  )
    .map(([key, label]) => {
      const hours = temporaryHours[key]

      if (!hours?.is_open) {
        return null
      }

      const open = formatTemporaryHour(hours.open)
      const close = formatTemporaryHour(hours.close)

      return open && close ? `${label} ${open} - ${close}` : label
    })
    .filter((label) => label !== null)
}

function getChangeRecurrenceLabel(change: AvailabilityChangeFields): string {
  return change?.repeats_annually ? 'Repeats every year' : 'One time'
}

function getEffectLabels(change: AvailabilityChangeFields): string[] {
  if (Array.isArray(change.effect_labels) && change.effect_labels.length > 0) {
    return change.effect_labels.map(formatAvailabilityEffectLabel)
  }

  const labels: string[] = []

  if (change.store_closed) {
    labels.push('Store closed')
  } else {
    if (change.pickup_paused) labels.push('Pickup pause')
    if (change.delivery_paused) labels.push('Delivery pause')
  }

  if (change.designers_choice_only) {
    labels.push("Designer's Choice only")
  }

  if (change.temporary_hours) {
    const hasPickupHours = Boolean(change.temporary_hours?.pickup?.is_open)
    const hasDeliveryHours = Boolean(change.temporary_hours?.delivery?.is_open)

    if (hasPickupHours && hasDeliveryHours) {
      labels.push('Temporary pickup and delivery hours')
    } else if (hasPickupHours) {
      labels.push('Temporary pickup hours')
    } else if (hasDeliveryHours) {
      labels.push('Temporary delivery hours')
    } else {
      labels.push('Temporary hours')
    }
  }

  return labels.length > 0 ? labels : ['Availability change']
}

function getChangeDisplayTitle(
  change: AvailabilityChangeFields,
  { showLocationDetails = true }: { showLocationDetails?: boolean } = {}
): string {
  const effectTitle = getEffectLabels(change).join(', ')

  return showLocationDetails ? `${effectTitle} · ${getChangeLocationLabel(change)}` : effectTitle
}

function ChangeIcon({ change }: { change: AvailabilityChangeFields }) {
  switch (getPrimaryEffectKey(change)) {
    case 'store_closed':
    case 'pickup_delivery_unavailable':
      return <Ban className="size-3 shrink-0" />
    case 'pickup_paused':
      return <ShoppingBasket className="size-3 shrink-0" />
    case 'delivery_paused':
      return <Truck className="size-3 shrink-0" />
    case 'designers_choice_only':
      return <Flower2 className="size-3 shrink-0" />
    case 'temporary_hours':
      return <Clock className="size-3 shrink-0" />
    case 'availability_change':
      return <CalendarSync className="size-3 shrink-0" />
  }
}

function getDayGridLinkProps(dayKey: string): AppLinkProps {
  return {
    search: (prev: Record<string, unknown>) => ({ ...prev, layout: 'list', date: dayKey }),
  }
}

function normalizeDateRange(
  startDate: string,
  endDate: string = startDate
): AvailabilityCalendarDateRange | null {
  const start = dayjs(startDate)
  const end = dayjs(endDate)

  if (!start.isValid() || !end.isValid()) {
    return null
  }

  return start.isAfter(end, 'day')
    ? {
        startDate: end.format('YYYY-MM-DD'),
        endDate: start.format('YYYY-MM-DD'),
      }
    : {
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
      }
}

function dayIsInRange(
  day: Dayjs,
  selection: AvailabilityCalendarDateRange | null | undefined
): boolean {
  const normalizedRange = selection
    ? normalizeDateRange(selection.startDate, selection.endDate)
    : null

  if (!normalizedRange) {
    return false
  }

  const currentDay = day.startOf('day')
  const startDay = dayjs(normalizedRange.startDate)
  const endDay = dayjs(normalizedRange.endDate)

  return (
    (currentDay.isSame(startDay, 'day') || currentDay.isAfter(startDay, 'day')) &&
    (currentDay.isSame(endDay, 'day') || currentDay.isBefore(endDay, 'day'))
  )
}

function getSelectionAnchor(event: SelectionAnchorEvent): AvailabilityCalendarAnchor | null {
  if (
    Number.isFinite(event.clientX) &&
    Number.isFinite(event.clientY) &&
    (event.clientX !== 0 || event.clientY !== 0)
  ) {
    return {
      left: event.clientX,
      top: event.clientY,
    }
  }

  const rect = event.currentTarget?.getBoundingClientRect?.()

  if (!rect) {
    return null
  }

  return {
    left: rect.left + rect.width / 2,
    top: rect.bottom + 8,
  }
}

function getChangeCalendarRange(
  change: AvailabilityChangeFields
): { startDay: Dayjs; endDay: Dayjs } | null {
  const start = getChangeStart(change)
  const end = getChangeEnd(change)

  if (!start || !end) {
    return null
  }

  return {
    startDay: start.startOf('day'),
    endDay: end.startOf('day'),
  }
}

function getChangeKey(change: AvailabilityChangeFields): string {
  return String(
    change.id ?? `${change.change_id}-${change.location?.id}-${change.starts_at ?? change.date}`
  )
}

function getChangeGroupKey(change: AvailabilityChangeFields): string {
  const start = getChangeStart(change)?.format('YYYY-MM-DD') ?? ''
  const end = getChangeEnd(change)?.format('YYYY-MM-DD') ?? ''

  return [
    change.change_id ?? change.id,
    start,
    end,
    Boolean(change.store_closed),
    Boolean(change.pickup_paused),
    Boolean(change.delivery_paused),
    Boolean(change.designers_choice_only),
    JSON.stringify(change.temporary_hours ?? null),
  ].join('|')
}

// The calendar sends one occurrence per location, and each names every location of its change, so a
// group keeps its first occurrence.
function groupCalendarChanges(
  changes: AvailabilityCalendarOccurrence[]
): AvailabilityCalendarOccurrence[] {
  const groupedChanges = new Map<string, AvailabilityCalendarOccurrence>()

  changes.forEach((change) => {
    const key = getChangeGroupKey(change)

    if (!groupedChanges.has(key)) {
      groupedChanges.set(key, { ...change, id: key })
    }
  })

  return [...groupedChanges.values()]
}

function buildWeekSegments(
  changes: AvailabilityCalendarOccurrence[],
  weekDays: Dayjs[]
): CalendarSegment[] {
  const firstDay = weekDays[0]
  const lastDay = weekDays[6]

  if (!firstDay || !lastDay) {
    return []
  }

  const weekStart = firstDay.startOf('day')
  const weekEnd = lastDay.startOf('day')
  const laneEndIndexes: number[] = []

  return changes
    .map((change) => {
      const range = getChangeCalendarRange(change)

      if (!range || range.endDay.isBefore(weekStart) || range.startDay.isAfter(weekEnd)) {
        return null
      }

      const segmentStart = range.startDay.isBefore(weekStart) ? weekStart : range.startDay
      const segmentEnd = range.endDay.isAfter(weekEnd) ? weekEnd : range.endDay

      return {
        change,
        key: getChangeKey(change),
        startIndex: segmentStart.diff(weekStart, 'day'),
        endIndex: segmentEnd.diff(weekStart, 'day'),
        continuesBefore: range.startDay.isBefore(weekStart),
        continuesAfter: range.endDay.isAfter(weekEnd),
      }
    })
    .filter((segment) => segment !== null)
    .sort((left, right) => {
      if (left.startIndex !== right.startIndex) return left.startIndex - right.startIndex
      if (left.endIndex !== right.endIndex) return right.endIndex - left.endIndex

      return getChangeDisplayTitle(left.change).localeCompare(getChangeDisplayTitle(right.change))
    })
    .map((segment) => {
      const lane = laneEndIndexes.findIndex((endIndex) => segment.startIndex > endIndex)
      const nextLane = lane === -1 ? laneEndIndexes.length : lane
      laneEndIndexes[nextLane] = segment.endIndex

      return { ...segment, lane: nextLane }
    })
}

function getHiddenCountsByDay(segments: CalendarSegment[]): Record<number, number> {
  return segments
    .filter((segment) => segment.lane >= visibleEventLanes)
    .reduce<Record<number, number>>((counts, segment) => {
      for (let dayIndex = segment.startIndex; dayIndex <= segment.endIndex; dayIndex += 1) {
        counts[dayIndex] = (counts[dayIndex] ?? 0) + 1
      }

      return counts
    }, {})
}

function AvailabilityChangeDetails({
  change,
  showLocationDetails = true,
}: {
  change: AvailabilityChangeFields
  showLocationDetails?: boolean
}) {
  const locationLabel = getChangeLocationLabel(change)
  const temporaryHoursPreviewLabels = getTemporaryHoursPreviewLabels(change)
  const badgeLabels =
    temporaryHoursPreviewLabels.length > 0 ? temporaryHoursPreviewLabels : getEffectLabels(change)

  return (
    <div className="space-y-2">
      <div className="flex min-w-0 items-center gap-2">
        <ChangeIcon change={change} />
        <Typography variant="labelStrong" className="truncate">
          {getEffectLabels(change).join(', ')}
        </Typography>
      </div>
      <div className="space-y-1">
        {showLocationDetails ? (
          <Typography variant="caption" as="div" className="flex items-center gap-2">
            <MapPin className="size-3 shrink-0" />
            <span className="truncate">{locationLabel}</span>
          </Typography>
        ) : null}
        <Typography variant="caption" as="div" className="flex items-center gap-2">
          <CalendarSync className="size-3 shrink-0" />
          <span>{getChangeDateRange(change)}</span>
        </Typography>
        <Typography variant="caption" as="div" className="flex items-center gap-2">
          <CalendarSync className="size-3 shrink-0" />
          <span>{getChangeRecurrenceLabel(change)}</span>
        </Typography>
      </div>
      <div className="flex flex-wrap gap-1">
        {badgeLabels.map((label) => (
          <Badge key={label} variant="muted" size="xs">
            {label}
          </Badge>
        ))}
      </div>
    </div>
  )
}

function AvailabilityChangeSegment({
  onChangeClick,
  onChangePointerUp,
  segment,
  showLocationDetails = true,
}: {
  onChangeClick?: ((change: AvailabilityCalendarOccurrence) => void) | undefined
  onChangePointerUp?: ((event: ReactPointerEvent<HTMLButtonElement>) => void) | undefined
  segment: CalendarSegment
  showLocationDetails?: boolean | undefined
}) {
  const { change } = segment
  const presentation = getAvailabilityChangePresentation(getPrimaryEffectKey(change))
  const locationLabel = getChangeLocationLabel(change)
  const displayTitle = getChangeDisplayTitle(change, { showLocationDetails })
  const [hoverPosition, setHoverPosition] = useState<{ left: number; top: number } | null>(null)

  const updateHoverPosition = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const tooltipWidth = 288
    const tooltipHeight = 172
    const viewportPadding = 16
    const offset = 14
    const viewportWidth = window.innerWidth || 1024
    const viewportHeight = window.innerHeight || 768
    const left =
      event.clientX + tooltipWidth + offset > viewportWidth - viewportPadding
        ? event.clientX - tooltipWidth - offset
        : event.clientX + offset
    const top =
      event.clientY + tooltipHeight + offset > viewportHeight - viewportPadding
        ? event.clientY - tooltipHeight - offset
        : event.clientY + offset

    setHoverPosition({
      left: Math.max(viewportPadding, left),
      top: Math.max(viewportPadding, top),
    })
  }

  const handleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur()
    setHoverPosition(null)
    onChangeClick?.(change)
  }

  return (
    <>
      <button
        type="button"
        data-raw-button-owner="availability-calendar-event"
        aria-label={`${displayTitle}, ${getChangeDateRange(change)}`}
        data-location-id={change.location?.id ?? locationLabel}
        data-effect={getPrimaryEffectKey(change)}
        className={cn(
          'z-10 mx-1 flex h-7 min-w-0 items-center gap-1.5 rounded-xl border px-2 text-left text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          presentation.calendarClassName,
          segment.continuesBefore && 'rounded-l-none border-l-0',
          segment.continuesAfter && 'rounded-r-none border-r-0'
        )}
        style={{
          gridColumn: `${segment.startIndex + 1} / ${segment.endIndex + 2}`,
          gridRow: 1,
          marginTop: eventLaneOffset + segment.lane * eventLaneHeight,
        }}
        onClick={handleClick}
        onPointerUp={onChangePointerUp}
        onMouseEnter={updateHoverPosition}
        onMouseMove={updateHoverPosition}
        onMouseLeave={() => setHoverPosition(null)}
      >
        <ChangeIcon change={change} />
        <span className="min-w-0 truncate">{displayTitle}</span>
      </button>
      {hoverPosition ? (
        <div
          className={cn(
            popoverContentVariants({
              size: 'default',
              padding: 'compact',
              elevation: 'strong',
              overflow: 'hidden',
            }),
            'pointer-events-none fixed'
          )}
          style={hoverPosition}
        >
          <AvailabilityChangeDetails change={change} showLocationDetails={showLocationDetails} />
        </div>
      ) : null}
    </>
  )
}

function CalendarDayCell({
  day,
  isCurrentMonth,
  isSelected,
  isToday,
  isWeekend,
  onDayPointerDown,
  onDayPointerEnter,
  onDayPointerUp,
  rowHeight,
}: {
  day: Dayjs
  isCurrentMonth: boolean
  isSelected: boolean
  isToday: boolean
  isWeekend: boolean
  onDayPointerDown?: DayPointerHandler | undefined
  onDayPointerEnter?: DayPointerHandler | undefined
  onDayPointerUp?: DayPointerHandler | undefined
  rowHeight: number
}) {
  const dayKey = day.format('YYYY-MM-DD')
  const dayLabelClassName = cn(
    'flex h-7 w-full items-center gap-1.5 rounded px-2 text-left text-sm font-medium',
    isToday
      ? 'bg-action text-action-foreground hover:bg-action-hover'
      : isCurrentMonth
        ? 'text-foreground'
        : 'text-muted-foreground'
  )
  const dayCellClassName = cn(
    'relative z-0 rounded-md border border-border/65 bg-card p-1',
    !isCurrentMonth && 'border-border/40 bg-elevated',
    isWeekend && isCurrentMonth && 'bg-elevated',
    isSelected
      ? 'border-selection-border/45 bg-accentSecondary ring-1 ring-inset ring-selection-border/20 hover:border-selection-border hover:bg-accentSecondary'
      : isCurrentMonth
        ? 'hover:border-selection-border/30 hover:bg-muted'
        : 'hover:border-selection-border/20 hover:bg-muted'
  )
  const dayInteractiveClassName =
    'absolute inset-0 flex cursor-pointer items-start rounded-md p-1 text-left outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]'

  const dayLinkClassName = cn(dayLabelClassName, 'cursor-pointer p-1')

  return (
    <div
      className={dayCellClassName}
      style={{ gridColumn: day.day() + 1, gridRow: 1, minHeight: rowHeight }}
    >
      {onDayPointerDown ? (
        <button
          type="button"
          data-raw-button-owner="availability-calendar-day"
          aria-label={isToday ? `${day.format('D')} Today` : undefined}
          className={dayInteractiveClassName}
          onPointerDown={(event) => onDayPointerDown(dayKey, event)}
          onPointerEnter={(event) => onDayPointerEnter?.(dayKey, event)}
          onPointerUp={(event) => onDayPointerUp?.(dayKey, event)}
        >
          <span className={dayLabelClassName}>
            <span className="leading-none">{day.format('D')}</span>
            {isToday ? <span className="text-[11px] leading-none font-semibold">Today</span> : null}
          </span>
        </button>
      ) : (
        <Link
          {...getDayGridLinkProps(dayKey)}
          aria-label={isToday ? `${day.format('D')} Today` : undefined}
          className={dayLinkClassName}
        >
          <span className="leading-none">{day.format('D')}</span>
          {isToday ? <span className="text-[11px] leading-none font-semibold">Today</span> : null}
        </Link>
      )}
    </div>
  )
}

function CalendarWeek({
  isLoading,
  onDayPointerDown,
  onDayPointerEnter,
  onDayPointerUp,
  onChangePointerUp,
  onChangeClick,
  changes,
  selectedRange,
  showLocationDetails,
  startOfMonth,
  today,
  weekDays,
}: {
  isLoading: boolean
  onDayPointerDown?: DayPointerHandler | undefined
  onDayPointerEnter?: DayPointerHandler | undefined
  onDayPointerUp?: DayPointerHandler | undefined
  onChangePointerUp?: ((event: ReactPointerEvent<HTMLButtonElement>) => void) | undefined
  onChangeClick?: ((change: AvailabilityCalendarOccurrence) => void) | undefined
  changes: AvailabilityCalendarOccurrence[]
  selectedRange: AvailabilityCalendarDateRange | null | undefined
  showLocationDetails: boolean
  startOfMonth: Dayjs
  today: Dayjs
  weekDays: Dayjs[]
}) {
  const segments = buildWeekSegments(changes, weekDays)
  const visibleSegments = segments.filter((segment) => segment.lane < visibleEventLanes)
  const hiddenCountsByDay = getHiddenCountsByDay(segments)
  const visibleLaneCount = Math.min(
    visibleEventLanes,
    Math.max(1, ...visibleSegments.map((segment) => segment.lane + 1))
  )
  const rowHeight = Math.max(136, eventLaneOffset + visibleLaneCount * eventLaneHeight + 14)

  return (
    <div className="grid grid-cols-7 gap-1" style={{ minHeight: rowHeight }}>
      {weekDays.map((day) => {
        const isCurrentMonth = day.isSame(startOfMonth, 'month')
        const isToday = day.isSame(today, 'day')
        const isWeekend = day.day() === 0 || day.day() === 6

        return (
          <CalendarDayCell
            key={day.format('YYYY-MM-DD')}
            day={day}
            isCurrentMonth={isCurrentMonth}
            isSelected={dayIsInRange(day, selectedRange)}
            isToday={isToday}
            isWeekend={isWeekend}
            onDayPointerDown={onDayPointerDown}
            onDayPointerEnter={onDayPointerEnter}
            onDayPointerUp={onDayPointerUp}
            rowHeight={rowHeight}
          />
        )
      })}

      {isLoading ? (
        <Skeleton
          className="z-10 mx-2 h-7"
          style={{
            gridColumn: '1 / 8',
            gridRow: 1,
            marginTop: eventLaneOffset,
          }}
        />
      ) : (
        visibleSegments.map((segment) => (
          <AvailabilityChangeSegment
            key={`${segment.key}-${segment.startIndex}`}
            onChangeClick={onChangeClick}
            onChangePointerUp={onChangePointerUp}
            segment={segment}
            showLocationDetails={showLocationDetails}
          />
        ))
      )}

      {!isLoading
        ? Object.entries(hiddenCountsByDay).map(([dayIndex, count]) => {
            const day = weekDays[Number(dayIndex)]

            if (!day) {
              return null
            }

            const dayKey = day.format('YYYY-MM-DD')

            return (
              <Link
                key={dayKey}
                {...getDayGridLinkProps(dayKey)}
                className="group z-10 mx-2 text-center"
                style={{
                  gridColumn: `${Number(dayIndex) + 1} / ${Number(dayIndex) + 2}`,
                  gridRow: 1,
                  marginTop: eventLaneOffset + visibleEventLanes * eventLaneHeight,
                }}
              >
                <Typography variant="caption" interaction="hoverUnderline">
                  +{count} more
                </Typography>
              </Link>
            )
          })
        : null}
    </div>
  )
}

function EffectLegend({
  hasActiveFilters = false,
  monthLabel,
  onClearFilters,
  changes,
}: {
  hasActiveFilters?: boolean
  monthLabel: string
  onClearFilters?: (() => void) | undefined
  changes: AvailabilityCalendarOccurrence[]
}) {
  const legendItems = effectLegendOrder.filter((effectKey) =>
    changes.some((change) => getPrimaryEffectKey(change) === effectKey)
  )
  const hasLegendItems = legendItems.length > 0
  const statusDescription = hasActiveFilters
    ? 'No matching changes. Try a different search or filter.'
    : `No scheduled availability changes for ${monthLabel}.`

  return (
    <div
      data-testid="availability-calendar-legend"
      className={cn(
        'flex min-h-9 flex-wrap items-center gap-x-3 gap-y-2 rounded-md border px-3 py-2',
        hasLegendItems ? 'border-border/60 bg-card/70' : 'border-border/50 bg-muted/20'
      )}
      aria-label={hasLegendItems ? 'Availability color legend' : 'Availability calendar status'}
    >
      {hasLegendItems ? (
        <>
          <Typography variant="label" tone="muted">
            Scheduled
          </Typography>
          {legendItems.map((effectKey) => {
            const presentation = getAvailabilityChangePresentation(effectKey)

            return (
              <div key={effectKey} className="flex min-w-0 items-center gap-1.5">
                <span
                  className={cn(
                    'size-2.5 shrink-0 rounded-full',
                    presentation.legendIndicatorClassName
                  )}
                />
                <Typography variant="caption" weight="medium" className="max-w-56 truncate">
                  {presentation.label}
                </Typography>
              </div>
            )
          })}
        </>
      ) : (
        <>
          <Typography variant="label" tone="muted">
            {statusDescription}
          </Typography>
          {hasActiveFilters && onClearFilters ? (
            <Button
              type="button"
              variant="ghost"
              size="micro"
              className="ml-auto"
              onClick={onClearFilters}
            >
              Clear filters
            </Button>
          ) : null}
        </>
      )}
    </div>
  )
}

export interface AvailabilityChangesMonthViewProps {
  changes?: AvailabilityCalendarOccurrence[]
  /** `YYYY-MM-DDtoYYYY-MM-DD`; its start anchors the month when `month` is missing. */
  dateRange?: string
  hasActiveFilters?: boolean
  /** `YYYY-MM` */
  month?: string
  isLoading?: boolean
  onClearFilters?: () => void
  /** Without it, days link to their list instead of selecting a range. */
  onSelectDateRange?: ((selection: AvailabilityCalendarRangeSelection) => void) | undefined
  onChangeClick?: ((change: AvailabilityCalendarOccurrence) => void) | undefined
  selectedRange?: AvailabilityCalendarDateRange | null
  showLocationDetails?: boolean
}

export function AvailabilityChangesMonthView({
  changes = [],
  dateRange,
  hasActiveFilters = false,
  month,
  isLoading = false,
  onClearFilters,
  onSelectDateRange,
  onChangeClick,
  selectedRange,
  showLocationDetails = true,
}: AvailabilityChangesMonthViewProps): ReactElement {
  const [dragSelection, setDragSelection] = useState<AvailabilityCalendarDateRange | null>(null)
  const dragSelectionRef = useRef<AvailabilityCalendarDateRange | null>(null)
  const dragAnchorRef = useRef<AvailabilityCalendarAnchor | null>(null)
  const today = dayjs()
  const monthAnchorDate = month ? `${month}-01` : dateRange?.split('to')[0]
  const startOfMonth = monthAnchorDate
    ? dayjs(monthAnchorDate).startOf('month')
    : today.startOf('month')
  const firstDayOfMonth = startOfMonth.day()
  const startDate = startOfMonth.subtract(firstDayOfMonth, 'day')
  const calendarDays = Array.from({ length: 42 }, (_, index) => startDate.add(index, 'day'))
  const calendarWeeks = Array.from({ length: 6 }, (_, index) =>
    calendarDays.slice(index * 7, index * 7 + 7)
  )
  const groupedChanges = groupCalendarChanges(changes)
  const activeSelection = dragSelection ?? selectedRange
  const monthLabel = startOfMonth.format('MMMM YYYY')
  const clearDragSelection = useCallback(() => {
    dragSelectionRef.current = null
    dragAnchorRef.current = null
    setDragSelection(null)
  }, [])

  const handleDayPointerDown: DayPointerHandler | undefined = onSelectDateRange
    ? (dayKey, event) => {
        if (event.button !== 0) {
          return
        }

        event.preventDefault()
        const nextSelection = { startDate: dayKey, endDate: dayKey }

        dragSelectionRef.current = nextSelection
        dragAnchorRef.current = getSelectionAnchor(event)
        setDragSelection(nextSelection)
      }
    : undefined
  const handleDayPointerEnter: DayPointerHandler | undefined = onSelectDateRange
    ? (dayKey, event) => {
        const currentSelection = dragSelectionRef.current

        if (!currentSelection) {
          return
        }

        const nextSelection = { ...currentSelection, endDate: dayKey }

        dragSelectionRef.current = nextSelection
        dragAnchorRef.current = getSelectionAnchor(event)
        setDragSelection(nextSelection)
      }
    : undefined
  const handleDayPointerUp: DayPointerHandler | undefined = onSelectDateRange
    ? (dayKey, event) => {
        if (event.button !== 0) {
          clearDragSelection()
          return
        }

        const currentSelection = dragSelectionRef.current
        if (!currentSelection) {
          return
        }

        const range = normalizeDateRange(currentSelection?.startDate ?? dayKey, dayKey)

        clearDragSelection()

        if (!range) {
          return
        }

        onSelectDateRange({
          ...range,
          anchor: getSelectionAnchor(event),
        })
      }
    : undefined
  const handleChangePointerUp = onSelectDateRange
    ? (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) {
          clearDragSelection()
          return
        }

        if (!dragSelectionRef.current) {
          return
        }

        clearDragSelection()
      }
    : undefined

  useEffect(() => {
    if (!onSelectDateRange) {
      return undefined
    }

    const handleWindowPointerUp = (event: PointerEvent) => {
      if (event.button !== 0) {
        clearDragSelection()
        return
      }

      const currentSelection = dragSelectionRef.current

      if (!currentSelection) {
        return
      }

      const range = normalizeDateRange(currentSelection.startDate, currentSelection.endDate)
      const anchor = getSelectionAnchor(event) ?? dragAnchorRef.current

      clearDragSelection()

      if (!range) {
        return
      }

      onSelectDateRange({
        ...range,
        anchor,
      })
    }

    window.addEventListener('pointerup', handleWindowPointerUp)

    return () => window.removeEventListener('pointerup', handleWindowPointerUp)
  }, [clearDragSelection, onSelectDateRange])

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[900px] space-y-1">
        <EffectLegend
          hasActiveFilters={hasActiveFilters}
          monthLabel={monthLabel}
          onClearFilters={onClearFilters}
          changes={groupedChanges}
        />

        <div className="grid grid-cols-7 gap-1">
          {dayNames.map((dayName) => (
            <div
              key={dayName}
              className="p-2 text-center text-sm font-medium text-muted-foreground"
            >
              {dayName}
            </div>
          ))}
        </div>

        {calendarWeeks.map((weekDays) => (
          <CalendarWeek
            key={weekDays[0]?.format('YYYY-MM-DD')}
            weekDays={weekDays}
            changes={groupedChanges}
            startOfMonth={startOfMonth}
            today={today}
            isLoading={isLoading}
            onDayPointerDown={handleDayPointerDown}
            onDayPointerEnter={handleDayPointerEnter}
            onDayPointerUp={handleDayPointerUp}
            onChangePointerUp={handleChangePointerUp}
            onChangeClick={onChangeClick}
            selectedRange={activeSelection}
            showLocationDetails={showLocationDetails}
          />
        ))}

        {onSelectDateRange ? (
          <Typography
            variant="caption"
            weight="medium"
            as="div"
            className="flex items-center justify-start gap-1.5 px-1 pt-1"
          >
            <MousePointer2 className="size-3.5" />
            <span>Click and drag across multiple days to select a date range.</span>
          </Typography>
        ) : null}
      </div>
    </div>
  )
}
